import type { MediaPlanPreviewNotice } from '../../../api/ai-media-plan-chat-types';

/**
 * Budget-integrity primitive (platform-agnostic, pure): does the sum of the per-campaign budgets
 * reconcile to the line-item price? Lives in `shared` so BOTH the backend (single-campaign and the
 * N-campaign split reconciliation) and the frontend config card use the SAME tolerance — a single
 * source of truth. Tolerance is a small fraction of the price (with an absolute floor) so sub-cent
 * rounding doesn't trip a false mismatch.
 */
export function reconcileBudgetToPrice(params: {
  lineItemPrice: number;
  campaignBudgetsTotal: number;
  toleranceRatio?: number;
}): { ok: boolean; difference: number } {
  const { lineItemPrice, campaignBudgetsTotal } = params;
  const toleranceRatio = params.toleranceRatio ?? 0.01;
  const tolerance = Math.max(Math.abs(lineItemPrice) * toleranceRatio, 0.5);
  const difference = campaignBudgetsTotal - lineItemPrice;
  return { ok: Math.abs(difference) <= tolerance, difference };
}

/**
 * Convenience wrapper: returns a `budget_mismatch` preview notice when the campaign budgets do not
 * reconcile to the line-item price, else null. The numbers are rounded to whole cents for display.
 */
export function budgetMismatchNotice(params: {
  lineItemPrice: number;
  campaignBudgetsTotal: number;
  toleranceRatio?: number;
}): MediaPlanPreviewNotice | null {
  const { ok, difference } = reconcileBudgetToPrice(params);
  if (ok) return null;
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    type: 'budget_mismatch',
    line_item_price: round(params.lineItemPrice),
    campaigns_total: round(params.campaignBudgetsTotal),
    difference: round(difference)
  };
}
